<template>
  <div>
    <div v-if="step === 'upload'">
      <b-row>
        <b-col
          cols="12"
          :md="isScanning ? 12 : 8"
        >
          <b-card>
            <div class="mb-1 font-weight-bolder">
              Image To Scan
            </div>
            <b-row v-if="!selectedFile">
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-button
                  block
                  class="scan-upload-button d-flex align-items-center justify-content-start"
                  @click="triggerFilePick(false)"
                >
                  <div class="scan-upload-icon-wrapper mr-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      viewBox="0 1 23 24"
                      fill="#049CFB"
                      stroke="#ffffff"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-image-plus-icon lucide-image-plus"
                    ><path d="M16 5h6" /><path d="M19 2v6" /><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /><circle
                      cx="9"
                      cy="9"
                      r="2"
                    /></svg>
                  </div>
                  Upload an Image</b-button>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <b-button
                  block
                  class="scan-upload-button d-flex align-items-center justify-content-start"
                  @click="openCameraModal"
                >
                  <div class="scan-upload-icon-wrapper mr-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      viewBox="0 1 23 24"
                      fill="#049CFB"
                      stroke="#ffffff"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-camera-icon lucide-camera"
                    ><path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" /><circle
                      cx="12"
                      cy="13"
                      r="3"
                    /></svg>
                  </div>
                  Take a Photo</b-button>
              </b-col>
            </b-row>
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="d-none"
              @change="onFileSelected"
            >
            <input
              ref="cameraInput"
              type="file"
              accept="image/*"
              capture="environment"
              class="d-none"
              @change="onFileSelected"
            >

            <div
              v-if="fileName"
              class="mt-1"
            >
              <b-row class="align-items-center">
                <b-col
                  cols="12"
                  :md="isUploading || isScanning ? 12 : 8"
                >
                  <div
                    class="p-1 border rounded d-flex align-items-center"
                  >
                    <div
                      class="d-flex align-items-center flex-grow-1"
                      style="min-width: 0;"
                    >
                      <img
                        v-if="imagePreviewUrl && !isScanning"
                        :src="imagePreviewUrl"
                        alt="Preview"
                        class="scan-image-preview mr-2"
                      >
                      <div
                        v-if="isScanning"
                        class="scan-icon-wrapper mr-1"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="#DAEDF9"
                          stroke="#049cfb"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-scan-line-icon lucide-scan-line"
                        ><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M7 12h10" /></svg>
                      </div>
                      <div
                        class="flex-grow-1"
                        style="min-width: 0;"
                      >
                        <div
                          v-if="!isScanning"
                          class="font-weight-bold text-truncate"
                        >
                          {{ fileName }}
                        </div>
                        <small
                          v-if="!isUploading && !isScanning"
                          class="text-secondary"
                        >{{ fileSizeLabel }}</small>
                        <div v-if="isUploading">
                          <small class="text-secondary">{{ uploadSizeLabel }}</small>
                          <b-progress
                            :value="uploadProgress"
                            max="100"
                            variant="primary"
                            height="0.6rem"
                          />
                        </div>
                        <div
                          v-if="isScanning"
                        >
                          <div class="font-weight-bold">
                            Scanning In Progress
                          </div>
                          <small>{{ Math.round(progress) }}%</small>
                          <b-progress
                            :value="progress"
                            max="100"
                            animated
                            variant="success"
                            height="0.6rem"
                          />
                        </div>
                      </div>
                    </div>
                    <b-button
                      size="sm"
                      variant="link"
                      class="text-body"
                      @click="clearSelectedFile"
                    >✕</b-button>
                  </div>
                </b-col>
                <b-col
                  v-if="!isUploading && !isScanning"
                  cols="12"
                  md="4"
                  class="text-md-right mt-1 mt-md-0"
                >
                  <b-button
                    variant="primary"
                    :disabled="!selectedFile || isUploading || isScanning"
                    @click="startScan"
                  >Upload & Scan</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
        <b-col
          v-if="!isScanning"
          cols="12"
          md="4"
        >
          <b-card>
            <div class="mb-1 font-weight-bolder">
              Download Template File
            </div>
            <b-button
              :href="defaultOcrTemplate"
              block
              class="scan-download-button d-flex align-items-center justify-content-start mb-1"
            >
              <div class="scan-download-template-wrapper mr-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 1 23 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-download-icon lucide-download"
                ><path d="M12 15V3" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /></svg>
              </div>
              Download</b-button>
          </b-card>
        </b-col>
      </b-row>

      <b-card v-if="!isScanning">
        <div class="mb-1 font-weight-bolder">
          Instructions
        </div>
        <ol class="mb-0 pl-1">
          <li>Write your letters clearly so they’re easy to read.</li>
          <li>Don’t upload blurry photos or unclear documents.</li>
          <li>
            Check if your details are filled correctly. You can edit fields or
            choose correct options after scan.
          </li>
        </ol>
      </b-card>
    </div>

    <div v-else-if="step === 'form'">
      <image-scan-order-form
        :order-settings="orderSettings"
        :waybill-placeholder="waybillPlaceholder"
        :waybill-pattern="waybillPattern"
        :pattern-title="patternTitle"
        :prefill="prefillData"
        @re-upload="resetToUploadStep"
      />
    </div>
    <b-modal
      id="camera-modal"
      ref="cameraModal"
      title="Take a Photo"
      size="lg"
      hide-footer
      @shown="onCameraModalShown"
      @hidden="stopCamera"
    >
      <div v-if="!cameraError">
        <video
          ref="videoPlayer"
          autoplay
          playsinline
          width="100%"
          class="mb-2"
        />
        <b-button
          variant="primary"
          block
          @click="captureImage"
        >
          Snap Photo
        </b-button>
      </div>
      <!-- Error message if camera access fails -->
      <div
        v-else
        class="text-danger text-center p-3"
      >
        <p>Could not access the camera. Please ensure you have a webcam and have granted permission.</p>
        <p><small>{{ cameraError }}</small></p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BProgress, BModal,
} from 'bootstrap-vue'
import { defaultOcrTemplate } from '@/constants/config'
import ErrorMessage from '@core/mixins/ErrorMessage'
import ImageScanOrderForm from '@/views/order-section/create/ImageScanOrderForm.vue'
import ocrRepository from '@/repository/ocrRepository'

export default {
  name: 'ImageScanUpload',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BProgress,
    BModal,
    ImageScanOrderForm,
  },
  mixins: [ErrorMessage],
  props: {
    orderSettings: {
      type: Object,
      default: null,
    },
    waybillPlaceholder: { type: Function, default: () => {} },
    waybillPattern: { type: Function, default: () => {} },
    patternTitle: { type: Function, default: () => {} },
  },
  data() {
    return {
      defaultOcrTemplate,
      step: 'upload',
      selectedFile: null,
      fileName: '',
      isUploading: false,
      uploadProgress: 0,
      uploadSizeLabel: '',
      isScanning: false,
      progress: 0,
      ocrText: '',
      prefillData: null,
      destinationCities: [],
      isCameraOpen: false,
      cameraStream: null,
      cameraError: '',
      imagePreviewUrl: null,
      activeScanId: 0,
    }
  },
  computed: {
    fileSizeLabel() {
      if (!this.selectedFile) return ''
      const size = this.selectedFile.size || 0
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
  },
  methods: {
    triggerFilePick(useCamera) {
      if (useCamera) {
        // On desktop, instead of clicking an input, we open our custom modal.
        this.openCameraModal()
      } else if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    async openCameraModal() {
      this.cameraError = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = 'getUserMedia is not supported by your browser.'
        this.$refs.cameraModal.show()
        return
      }

      try {
        // 1. Get the stream and store it in our data property
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        })

        // 2. Now, show the modal. The stream is NOT attached yet.
        this.$refs.cameraModal.show()
        this.isCameraOpen = true
      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          this.cameraError = 'Camera permission was denied. Please allow access in your browser settings.'
        } else {
          this.cameraError = `An error occurred while accessing the camera: ${err.name}`
        }
        // Show the modal even on error to display the message
        this.$refs.cameraModal.show()
      }
    },
    onCameraModalShown() {
      // Attach the stream only when the video element is guaranteed to be ready.
      const video = this.$refs.videoPlayer
      if (video && this.cameraStream) {
        video.srcObject = this.cameraStream

        // As a safeguard, also call play(). Some browsers need this.
        video.play().catch(() => {
          this.cameraError = 'Could not play the camera stream.'
        })
      }
    },
    stopCamera() {
      if (this.cameraStream) {
        // Stop all tracks in the stream
        this.cameraStream.getTracks().forEach(track => track.stop())
        this.cameraStream = null
      }
      this.isCameraOpen = false
    },
    // NEW: Method to capture the image from the video feed
    captureImage() {
      const video = this.$refs.videoPlayer
      if (!video) return

      // Create a canvas element to draw the image on
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Convert the canvas image to a Blob
      canvas.toBlob(blob => {
        // Create a File object from the Blob
        const file = new File([blob], 'webcam-photo.jpg', { type: 'image/jpeg' })

        // Use the captured file just like an uploaded one
        this.selectedFile = file
        this.fileName = file.name

        if (this.imagePreviewUrl) {
          URL.revokeObjectURL(this.imagePreviewUrl)
        }
        // Create a new URL for the captured photo
        this.imagePreviewUrl = URL.createObjectURL(file)

        // Close the modal and stop the camera
        this.$refs.cameraModal.hide()
      }, 'image/jpeg')
    },
    clearSelectedFile() {
      this.activeScanId += 1
      this.isUploading = false
      this.isScanning = false
      // Clean up the previous object URL to avoid memory leaks
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl)
      }

      this.selectedFile = null
      this.fileName = ''
      this.imagePreviewUrl = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      if (this.$refs.cameraInput) this.$refs.cameraInput.value = ''
    },
    onFileSelected(event) {
      const [file] = event.target.files || []
      if (file) {
        this.selectedFile = file
        this.fileName = file.name
        // Clean up the previous object URL in case user selects a new file
        if (this.imagePreviewUrl) {
          URL.revokeObjectURL(this.imagePreviewUrl)
        }
        // Create a new URL for the selected file
        this.imagePreviewUrl = URL.createObjectURL(file)
      }
    },
    formatBytes(bytes, decimals = 1) {
      if (!+bytes) return '0 B'

      const k = 1024
      const dm = decimals < 0 ? 0 : decimals
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
    },
    async startScan() {
      if (!this.selectedFile) return

      this.activeScanId += 1
      const currentScanId = this.activeScanId

      // Simulate an upload progress to match UI expectation
      this.isUploading = true
      this.uploadProgress = 0
      // Get the total size of the file in bytes
      const totalSizeInBytes = this.selectedFile.size
      // Create a formatted, human-readable string for the total size (e.g., "3.0 MB")
      const totalSizeFormatted = this.formatBytes(totalSizeInBytes)

      // Set the initial label before the interval starts
      this.uploadSizeLabel = `0 B of ${totalSizeFormatted}`

      await new Promise(resolve => {
        const start = Date.now()
        const interval = setInterval(() => {
          const elapsed = Date.now() - start
          // Update the progress percentage based on time
          this.uploadProgress = Math.min(
            100,
            Math.round((elapsed / 1200) * 100),
          )

          // Calculate the simulated number of bytes uploaded so far
          const uploadedBytes = (totalSizeInBytes * this.uploadProgress) / 100

          // Create the dynamic label using our helper function
          this.uploadSizeLabel = `${this.formatBytes(uploadedBytes)} of ${totalSizeFormatted}`

          if (this.uploadProgress >= 100) {
            // Ensure the final label shows the full, correct amount
            this.uploadSizeLabel = `${totalSizeFormatted} of ${totalSizeFormatted}`
            clearInterval(interval)
            resolve()
          }
        }, 80)
      })
      this.isUploading = false
      this.isScanning = true
      this.progress = 0
      try {
        const extractData = await ocrRepository.extractFileData(this.selectedFile)

        if (this.activeScanId !== currentScanId) {
          return // <-- Exit the function and do nothing
        }
        // Handle potential error response from API
        if (extractData.success === false) {
          this.showErrorMessage(extractData.error)
          this.isScanning = false
          return
        }
        this.prefillData = this.parseOrderFromText(extractData)
        this.step = 'form'
      } catch (e) {
        // fallback: show empty form
        this.step = 'form'
        this.convertAndNotifyError(e)
      }
      this.isScanning = false
    },
    parseOrderFromText(data) {
      const normalizePhone = phone => (phone ? String(phone).replace(/\s+/g, '') : '')

      return {
        merchant_business: data.merchant_details.merchant_business || '',
        waybill_number: data.package_details.waybill_no || '',
        order_no: data.package_details.order_no || '',
        cod: data.package_details.cod_amount,
        weight: data.package_details.weight,
        customer_name: data.customer_details.customer_name || '',
        customer_address: data.customer_details.address || '',
        customer_phone: normalizePhone(data.customer_details.primary_phone),
        customer_secondary_phone: normalizePhone(data.customer_details.secondary_phone),
        customer_email: data.customer_details.email,
        destination_city: data.origin_destination.destination_city || '',
        pickup_address: data.origin_destination.pickup_address,
        description: data.other_details.description || '',
        remark: data.other_details.remarks || '',
      }
    },
    resetToUploadStep() {
      // Use the existing method to clear file details
      this.clearSelectedFile()

      // Reset all other state variables to their initial values
      this.isUploading = false
      this.uploadProgress = 0
      this.uploadSizeLabel = ''
      this.isScanning = false
      this.progress = 0
      this.ocrText = ''
      this.prefillData = null

      // Switch the component's view back to the upload step
      this.step = 'upload'
    },
  },
}
</script>
